import { useEffect, useState } from "react";
import { Modal } from "./Modal";
import { kanbanOn } from "./kanbanLink";
import { loadCrmWeek, saveCrmProperties, type CrmWeekBoard } from "./week";

type DraftValue = { key: string; label: string; color: string };
type DraftProp = { key: string; label: string; values: DraftValue[] };
type ColorPick = { scope: "task" | "note"; propKey: string; valueKey: string; label: string; color: string };

export function PropertyButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold" onClick={() => setOpen(true)}>Propriétés</button>
      {open ? <PropertyDesk onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function PropertyDesk({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<DraftProp[]>([]);
  const [notes, setNotes] = useState<DraftValue[]>([]);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pick, setPick] = useState<ColorPick | null>(null);

  useEffect(() => {
    let alive = true;
    void loadCrmWeek()
      .then((board) => {
        if (!alive) return;
        setRows(toDraft(board.properties));
        setNotes(toNoteDraft(board.note_labels));
      })
      .catch((reason: unknown) => { if (alive) setError(reason instanceof Error ? reason.message : "Propriétés indisponibles."); })
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, []);

  function patch(key: string, next: DraftProp) {
    setRows((current) => current.map((row) => row.key === key ? next : row));
  }

  function setColor(pick: ColorPick, color: string) {
    if (pick.scope === "note") {
      setNotes((current) => current.map((item) => item.key === pick.valueKey ? { ...item, color } : item));
    } else {
      setRows((current) => current.map((row) => {
        if (row.key !== pick.propKey) return row;
        return { ...row, values: row.values.map((item) => item.key === pick.valueKey ? { ...item, color } : item) };
      }));
    }
    setPick((current) => current && current.scope === pick.scope && current.propKey === pick.propKey && current.valueKey === pick.valueKey ? { ...current, color } : current);
  }

  async function save(close: () => void) {
    const invalidTasks = rows.some((row) => !row.label.trim() || row.values.length === 0 || row.values.some((value) => !value.label.trim()));
    const invalidNotes = notes.length === 0 || notes.some((value) => !value.label.trim());
    if (invalidTasks || invalidNotes) {
      setError("Chaque propriété / pastille a un nom, et les notes ont au moins une pastille.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await saveCrmProperties(fromDraft(rows), fromNoteDraft(notes));
      close();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal onClose={onClose} sheetClass="crm-pop flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
        {(close) => (
          <>
            <div className="mb-3">
              <h2 className="text-lg font-semibold">Propriétés</h2>
              <p className="mt-1 text-sm text-slate-500">
                Deux catalogues distincts : les champs des tâches, et les pastilles des notes fiche. Aucun lien entre les deux.
                {kanbanOn() ? " Les propriétés tâches s’affichent sur la semaine dès le passage en autonome." : " Les propriétés tâches s’appliquent tout de suite."}
              </p>
            </div>
            {!ready ? (
              <div className="flex flex-1 items-center justify-center py-10" role="status" aria-label="Chargement des propriétés">
                <span className="crm-browse-loading-spin" aria-hidden />
              </div>
            ) : (
              <div className="min-h-0 flex-1 space-y-5 overflow-auto pr-1">
                <section>
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">Tâches</h3>
                    <p className="text-xs text-slate-500">Priorité teinte la carte. Autres champs libres sur la fiche tâche.</p>
                  </div>
                  <div className="space-y-3">
                    {rows.map((prop) => (
                      <section key={prop.key} className="rounded-2xl border border-slate-200 p-3">
                        <div className="flex items-center gap-2">
                          <input className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold" value={prop.label} aria-label="Nom de la propriété" onChange={(event) => patch(prop.key, { ...prop, label: event.target.value })} />
                          {prop.key === "importance" ? (
                            <span className="shrink-0 text-[11px] font-semibold text-slate-400">Priorité</span>
                          ) : (
                            <button type="button" className="text-xs font-semibold text-rose-700" onClick={() => setRows((current) => current.filter((row) => row.key !== prop.key))}>Retirer</button>
                          )}
                        </div>
                        <ul className="mt-2 space-y-2">
                          {prop.values.map((value) => (
                            <li key={value.key} className="flex items-center gap-2">
                              <button
                                type="button"
                                className="h-8 w-8 shrink-0 rounded-lg ring-1 ring-black/10"
                                style={{ background: value.color }}
                                aria-label={`Couleur de ${value.label || "la valeur"}`}
                                onClick={() => setPick({ scope: "task", propKey: prop.key, valueKey: value.key, label: value.label || "Valeur", color: value.color })}
                              />
                              <input className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-1.5 text-sm" value={value.label} aria-label="Nom de la valeur" onChange={(event) => patch(prop.key, { ...prop, values: prop.values.map((item) => item.key === value.key ? { ...item, label: event.target.value } : item) })} />
                              <button type="button" className="text-xs text-slate-400" aria-label="Retirer la valeur" disabled={prop.values.length < 2} onClick={() => patch(prop.key, { ...prop, values: prop.values.filter((item) => item.key !== value.key) })}>Retirer</button>
                            </li>
                          ))}
                        </ul>
                        <button type="button" className="mt-2 text-xs font-semibold text-slate-500" onClick={() => patch(prop.key, { ...prop, values: [...prop.values, { key: freshKey("val"), label: "", color: "#dbeafe" }] })}>+ Valeur</button>
                      </section>
                    ))}
                    <button type="button" className="text-sm font-semibold text-slate-500" onClick={() => setRows((current) => [...current, { key: freshKey("prop"), label: "", values: [{ key: freshKey("val"), label: "", color: "#dbeafe" }] }])}>+ Propriété tâche</button>
                  </div>
                </section>

                <section>
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">Notes fiche</h3>
                    <p className="text-xs text-slate-500">Pastilles internes aux notes uniquement (À faire, RDV…). Ne passent pas sur le Kanban.</p>
                  </div>
                  <section className="rounded-2xl border border-slate-200 p-3">
                    <ul className="space-y-2">
                      {notes.map((value) => (
                        <li key={value.key} className="flex items-center gap-2">
                          <button
                            type="button"
                            className="h-8 w-8 shrink-0 rounded-lg ring-1 ring-black/10"
                            style={{ background: value.color }}
                            aria-label={`Couleur de ${value.label || "la pastille"}`}
                            onClick={() => setPick({ scope: "note", propKey: "note_labels", valueKey: value.key, label: value.label || "Pastille", color: value.color })}
                          />
                          <input className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-1.5 text-sm" value={value.label} aria-label="Nom de la pastille" onChange={(event) => setNotes((current) => current.map((item) => item.key === value.key ? { ...item, label: event.target.value } : item))} />
                          <button type="button" className="text-xs text-slate-400" aria-label="Retirer la pastille" disabled={notes.length < 2} onClick={() => setNotes((current) => current.filter((item) => item.key !== value.key))}>Retirer</button>
                        </li>
                      ))}
                    </ul>
                    <button type="button" className="mt-2 text-xs font-semibold text-slate-500" onClick={() => setNotes((current) => [...current, { key: freshKey("note"), label: "", color: "#dbeafe" }])}>+ Pastille note</button>
                  </section>
                </section>
              </div>
            )}
            {error ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              <button type="button" className="crm-fill rounded-full px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={!ready || busy} onClick={() => void save(close)}>Enregistrer</button>
            </div>
          </>
        )}
      </Modal>
      {pick ? (
        <ColorModal
          label={pick.label}
          color={pick.color}
          onPick={(color) => setColor(pick, color)}
          onClose={() => setPick(null)}
        />
      ) : null}
    </>
  );
}

function ColorModal({ label, color, onPick, onClose }: { label: string; color: string; onPick: (color: string) => void; onClose: () => void }) {
  return (
    <Modal onClose={onClose} sheetClass="crm-pop w-full max-w-sm rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold">Couleur</h3>
              <p className="mt-0.5 text-sm text-slate-500">{label}</p>
            </div>
            <span className="h-8 w-8 rounded-lg ring-1 ring-black/10" style={{ background: color }} aria-hidden />
          </div>
          <div className="space-y-2">
            {PALETTE.map((family) => (
              <div key={family.label} className="flex items-center gap-2">
                <span className="w-14 shrink-0 text-xs font-medium text-slate-500">{family.label}</span>
                <div className="flex gap-1.5">
                  {family.shades.map((shade, index) => {
                    const on = sameColor(color, shade);
                    return (
                      <button
                        key={shade}
                        type="button"
                        aria-label={`${family.label}, ${SHADE_NAMES[index]}`}
                        aria-pressed={on}
                        className={on ? "h-9 w-9 rounded-lg ring-2 ring-slate-900 ring-offset-1" : "h-9 w-9 rounded-lg ring-1 ring-black/10"}
                        style={{ background: shade }}
                        onClick={() => { onPick(shade); close(); }}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}

function toDraft(properties: CrmWeekBoard["properties"]): DraftProp[] {
  return properties
    .filter((prop) => prop.key !== "statut" && prop.key !== "note_labels")
    .filter((prop) => prop.type !== "text" && Array.isArray(prop.values) && prop.values.length > 0)
    .map((prop) => ({
      key: prop.key,
      label: prop.label || prop.key,
      values: prop.values.map((value) => ({
        key: value.key,
        label: value.label || value.key,
        color: value.color?.bg && /^#[0-9a-fA-F]{6}$/.test(value.color.bg) ? value.color.bg : "#f3f4f6",
      })),
    }));
}

function toNoteDraft(rows: CrmWeekBoard["note_labels"] | undefined): DraftValue[] {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return DEFAULT_NOTE_LABELS.map((item) => ({ ...item }));
  return list
    .filter((value) => value.key !== "done" && value.key !== "fait")
    .map((value) => ({
      key: value.key,
      label: value.label || value.key,
      color: value.color?.bg && /^#[0-9a-fA-F]{6}$/.test(value.color.bg) ? value.color.bg : "#64748b",
    }));
}

function fromDraft(rows: DraftProp[]): CrmWeekBoard["properties"] {
  return rows.map((prop) => ({
    key: prop.key,
    label: prop.label.trim(),
    type: "select",
    values: prop.values.map((value) => ({
      key: value.key,
      label: value.label.trim(),
      color: { bg: value.color, text: ink(value.color) },
    })),
  }));
}

function fromNoteDraft(rows: DraftValue[]): CrmWeekBoard["note_labels"] {
  return rows.map((value) => ({
    key: value.key,
    label: value.label.trim(),
    color: { bg: value.color, text: ink(value.color) },
  }));
}

function freshKey(prefix: string): string {
  return `${prefix}_${Math.random().toString(16).slice(2, 10)}`;
}

const DEFAULT_NOTE_LABELS: DraftValue[] = [
  { key: "todo", label: "À faire", color: "#64748b" },
  { key: "inprogress", label: "En cours", color: "#2563eb" },
  { key: "pending", label: "En attente", color: "#d97706" },
  { key: "rdv", label: "RDV", color: "#1d4ed8" },
  { key: "important", label: "Important", color: "#e11d48" },
  { key: "suivi", label: "À suivre", color: "#d97706" },
  { key: "call", label: "Appeler", color: "#7c3aed" },
  { key: "quote", label: "Devis", color: "#0f766e" },
  { key: "unpaid", label: "Impayé", color: "#c2410c" },
];

const SHADE_NAMES = ["clair", "vif", "foncé"];

const PALETTE: Array<{ label: string; shades: [string, string, string] }> = [
  { label: "Gris", shades: ["#f3f4f6", "#94a3b8", "#334155"] },
  { label: "Bleu", shades: ["#dbeafe", "#3b82f6", "#1e3a8a"] },
  { label: "Cyan", shades: ["#cffafe", "#06b6d4", "#155e75"] },
  { label: "Vert", shades: ["#dcfce7", "#10b981", "#166534"] },
  { label: "Jaune", shades: ["#fef9c3", "#eab308", "#854d0e"] },
  { label: "Ambre", shades: ["#fef3c7", "#f59e0b", "#92400e"] },
  { label: "Rouge", shades: ["#fee2e2", "#ef4444", "#991b1b"] },
  { label: "Violet", shades: ["#ede9fe", "#8b5cf6", "#5b21b6"] },
];

function sameColor(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function ink(hex: string): string {
  const color = hex.replace("#", "");
  const red = Number.parseInt(color.slice(0, 2), 16);
  const green = Number.parseInt(color.slice(2, 4), 16);
  const blue = Number.parseInt(color.slice(4, 6), 16);
  if ([red, green, blue].some((part) => Number.isNaN(part))) return "#111827";
  return (red * 299 + green * 587 + blue * 114) / 1000 >= 128 ? "#000" : "#fff";
}
